/**
 * Service chat PUBLIC (visiteur anonyme) — endpoints /api/public/chat/*.
 *
 * Couche 1 — Transport (ADR-001). Aucune authentification : l'identité est le
 * `visitorId` (nanoid persistant en localStorage) envoyé en query ou en body
 * (cf. publicChatRoutes.ts côté API). `$fetch` direct, pas `apiCall` : pas
 * de Bearer ni d'organization-id à injecter.
 *
 * Les fonctions LÈVENT en cas d'échec réseau / non-2xx ; c'est le controller
 * (`usePublicChatController`) qui choisit de les absorber (le widget ne
 * doit jamais casser la page).
 */
import type { ChatMessage, ChatConversation } from "@donoor/api-contract";

/** Corps d'un message visiteur (nom/email optionnels du formulaire d'entrée). */
export interface PublicChatMessageBody {
  visitorId: string;
  message: string;
  name?: string;
  email?: string;
  /** Page du site où se trouve le visiteur : l'assistant IA adapte sa réponse. */
  page?: string;
  /** Résumé de la fiche affichée (cagnotte, adhésion, boutique, événement,
   *  association) — `useChatPageContext`. L'assistant aide en priorité dessus. */
  pageContexte?: string;
  /** Premier message adressé directement à l'équipe (pas d'assistant IA). */
  equipe?: boolean;
}

/** Ce que l'assistant IA propose à l'ouverture du chat. */
export interface AssistantPublic {
  /** L'assistant répond aux questions tapées (sinon : réponses de l'équipe). */
  actif: boolean;
  accueil: string;
  suggestions: { id: string; question: string }[];
  /** `true` quand accueil/suggestions sont adaptés à une fiche précise
   *  (cagnotte, adhésion…) : ces questions n'ont pas de réponse pré-écrite,
   *  un clic doit passer par le vrai modèle (pas `askSuggestion`/0 €). */
  contextuel: boolean;
}

// Enveloppes `{ success, data }` renvoyées par publicChatRoutes.ts.
type ListeConversations = { success: boolean; data: ChatConversation[] };
type ListeMessages = { success: boolean; data: ChatMessage[] };
type ConversationOuverte = {
  success: boolean;
  data: { conversation: ChatConversation; message: ChatMessage };
};
type MessageEnvoye = { success: boolean; data: ChatMessage };
type Assistant = { success: boolean; data: AssistantPublic };
type ConversationSeule = { success: boolean; data: ChatConversation };
type SuggestionRepondue = { success: boolean; data: { conversation: ChatConversation } | null };

function apiBase(): string {
  return useRuntimeConfig().public.apiBaseUrl as string;
}

function url(path: string): string {
  return `${apiBase()}/api/public/chat${path}`;
}

export const publicChatApi = {
  /** Conversations du visiteur (méta : membre assigné, curseur lu équipe). */
  listConversations(visitorId: string): Promise<ListeConversations> {
    return $fetch<ListeConversations>(url(`/conversations?visitorId=${visitorId}`), {
      method: "GET",
    });
  },

  /** Fil d'une conversation du visiteur. */
  listMessages(
    conversationId: number,
    visitorId: string,
  ): Promise<ListeMessages> {
    return $fetch<ListeMessages>(
      url(`/conversations/${conversationId}/messages?visitorId=${visitorId}`),
      { method: "GET" },
    );
  },

  /** Première prise de contact : ouvre la conversation avec ce message. */
  startConversation(body: PublicChatMessageBody): Promise<ConversationOuverte> {
    return $fetch<ConversationOuverte>(url("/conversations"), {
      method: "POST",
      body,
    });
  },

  /** Message suivant dans une conversation existante. */
  sendMessage(
    conversationId: number,
    body: PublicChatMessageBody,
  ): Promise<MessageEnvoye> {
    return $fetch<MessageEnvoye>(url(`/conversations/${conversationId}/messages`), {
      method: "POST",
      body,
    });
  },

  /** Accueil et questions toutes prêtes de l'assistant IA, dans la langue
   *  donnée — adaptées à la fiche affichée si `type` est fourni. */
  assistant(langue: string, type?: string): Promise<Assistant> {
    const qs = type
      ? `langue=${encodeURIComponent(langue)}&type=${encodeURIComponent(type)}`
      : `langue=${encodeURIComponent(langue)}`;
    return $fetch<Assistant>(url(`/assistant?${qs}`), { method: "GET" });
  },

  /** Question toute prête : la question et sa réponse écrite à l'avance s'ajoutent au fil. */
  askSuggestion(
    suggestionId: string,
    body: { visitorId: string; conversationId?: number; langue: string },
  ): Promise<SuggestionRepondue> {
    return $fetch<SuggestionRepondue>(url(`/suggestions/${encodeURIComponent(suggestionId)}`), {
      method: "POST",
      body,
    });
  },

  /** « Parler à l'équipe » : l'assistant IA laisse la main à l'équipe. */
  askTeam(
    conversationId: number,
    body: { visitorId: string; name?: string; email?: string },
  ): Promise<ConversationSeule> {
    return $fetch<ConversationSeule>(url(`/conversations/${conversationId}/equipe`), {
      method: "POST",
      body,
    });
  },

  /** Curseur « lu » côté visiteur. */
  async markRead(conversationId: number, visitorId: string): Promise<void> {
    await $fetch<unknown>(url(`/conversations/${conversationId}/read`), {
      method: "POST",
      body: { visitorId },
    });
  },
};
